import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonChip,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonSearchbar,
  IonText,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useEffect, useRef, useState } from "react";
import { ArtistSearchResult } from "../data/store/useSearch";
import { SearchService } from "../services/SearchService";

interface TransferResult {
  success: boolean;
  songCount: number;
  titles?: string[];
  fromArtist: { id: string; alias: string };
  toArtist: { id: string; alias: string };
  dryRun: boolean;
  message?: string;
}

interface ArtistPickerProps {
  label: string;
  selected: ArtistSearchResult | null;
  onSelect: (artist: ArtistSearchResult | null) => void;
}

const ArtistPicker: React.FC<ArtistPickerProps> = ({
  label,
  selected,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ArtistSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!query || query.length < 1) {
      setResults([]);
      return;
    }
    const reqId = ++reqIdRef.current;
    setSearching(true);
    SearchService.please()
      .searchArtists(query, 0)
      .then(({ artists }) => {
        if (reqId !== reqIdRef.current) return;
        setResults(
          (artists || []).filter(
            (a) => a.alias && a.alias.length > 1
          )
        );
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setResults([]);
      })
      .finally(() => {
        if (reqId !== reqIdRef.current) return;
        setSearching(false);
      });
  }, [query]);

  if (selected) {
    return (
      <IonItem lines="none">
        <IonLabel color="primary">{label}</IonLabel>
        <IonChip onClick={() => onSelect(null)}>
          <IonLabel>{selected.alias}</IonLabel>
          <IonIcon icon={closeOutline} aria-label="Remove selection" />
        </IonChip>
      </IonItem>
    );
  }

  return (
    <>
      <IonItem lines="none">
        <IonLabel color="primary">{label}</IonLabel>
      </IonItem>
      <IonItem>
        <IonSearchbar
          value={query}
          debounce={250}
          placeholder={`Search ${label.toLowerCase()}`}
          aria-label={`Search ${label.toLowerCase()}`}
          onIonChange={(e) => setQuery(e.detail.value || "")}
        />
      </IonItem>
      {query.length > 0 && (
        <IonList>
          {searching && results.length === 0 && (
            <IonItem lines="none">
              <IonNote>Searching…</IonNote>
            </IonItem>
          )}
          {!searching && results.length === 0 && (
            <IonItem lines="none">
              <IonNote>No matches</IonNote>
            </IonItem>
          )}
          {results.slice(0, 8).map((artist) => (
            <IonItem
              key={artist.id}
              button
              onClick={() => {
                onSelect(artist);
                setQuery("");
                setResults([]);
              }}
            >
              <IonLabel>{artist.alias}</IonLabel>
              <IonNote slot="end" style={{ fontSize: 12 }}>
                {artist.id}
              </IonNote>
            </IonItem>
          ))}
        </IonList>
      )}
    </>
  );
};

const TransferOwnership: React.FC = () => {
  const [fromArtist, setFromArtist] = useState<ArtistSearchResult | null>(null);
  const [toArtist, setToArtist] = useState<ArtistSearchResult | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TransferResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const fromArtistId = fromArtist?.id;
  const toArtistId = toArtist?.id;

  const canSubmit =
    !!fromArtistId && !!toArtistId && fromArtistId !== toArtistId;

  const runTransfer = async () => {
    if (!fromArtistId || !toArtistId) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const transfer = httpsCallable<
        { fromArtistId: string; toArtistId: string; dryRun: boolean },
        TransferResult
      >(getFunctions(), "transferSongOwnership");
      const res = await transfer({
        fromArtistId,
        toArtistId,
        dryRun,
      });
      setResult(res.data);
    } catch (err: any) {
      setError(err?.message || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (dryRun) {
      runTransfer();
    } else {
      setShowConfirm(true);
    }
  };

  return (
    <IonCard>
      <IonCardHeader color="primary">
        <IonCardTitle>Transfer Song Ownership</IonCardTitle>
        <IonCardSubtitle>
          Move all songs (and counts) from one artist to another.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        <ArtistPicker
          label="From Artist"
          selected={fromArtist}
          onSelect={setFromArtist}
        />

        <div style={{ height: 16 }} />

        <ArtistPicker
          label="To Artist"
          selected={toArtist}
          onSelect={setToArtist}
        />

        <IonItem style={{ marginTop: 16 }}>
          <IonCheckbox
            slot="start"
            checked={dryRun}
            onIonChange={(e) => setDryRun(e.detail.checked)}
          />
          <IonLabel>Dry run (preview without writing)</IonLabel>
        </IonItem>

        <div style={{ marginTop: 16 }}>
          <IonButtons>
            <IonButton
              expand="block"
              color={dryRun ? "favorite" : "danger"}
              disabled={!canSubmit || loading}
              onClick={handleSubmit}
            >
              {dryRun ? "Run Dry Run" : "Transfer Ownership"}
            </IonButton>
          </IonButtons>
          {!canSubmit && (fromArtistId || toArtistId) && (
            <IonNote color="medium">
              Pick two different artists to enable transfer.
            </IonNote>
          )}
        </div>

        {error && (
          <IonItem color="danger" lines="none" style={{ marginTop: 12 }}>
            <IonLabel className="ion-text-wrap">
              <h3>Error</h3>
              <p>{error}</p>
            </IonLabel>
          </IonItem>
        )}

        {result && (
          <IonCard style={{ marginTop: 12 }}>
            <IonCardHeader color={result.dryRun ? "favorite" : "success"}>
              <IonCardTitle>
                {result.dryRun ? "Dry Run Result" : "Transfer Complete"}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>
                  <strong>From:</strong>{" "}
                  {result.fromArtist.alias || result.fromArtist.id} (
                  {result.fromArtist.id})
                </p>
                <p>
                  <strong>To:</strong>{" "}
                  {result.toArtist.alias || result.toArtist.id} (
                  {result.toArtist.id})
                </p>
                <p>
                  <strong>
                    Songs {result.dryRun ? "would be" : ""} transferred:
                  </strong>{" "}
                  {result.songCount}
                </p>
                {result.message && <p>{result.message}</p>}
              </IonText>
              {result.titles && result.titles.length > 0 && (
                <IonList>
                  {result.titles.map((t, idx) => (
                    <IonItem key={idx}>
                      <IonLabel>{t}</IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </IonCardContent>
          </IonCard>
        )}

        <IonAlert
          isOpen={showConfirm}
          onDidDismiss={() => setShowConfirm(false)}
          header="Confirm Transfer"
          message={`This will permanently move all songs from "${fromArtist?.alias}" to "${toArtist?.alias}". This cannot be undone. Continue?`}
          buttons={[
            { text: "Cancel", role: "cancel" },
            {
              text: "Transfer",
              cssClass: "alert-button-danger",
              handler: () => {
                runTransfer();
              },
            },
          ]}
        />

        <IonLoading isOpen={loading} message="Transferring songs..." />
      </IonCardContent>
    </IonCard>
  );
};

export default TransferOwnership;
